import React, { useState } from "react";
import { Link } from "react-router-dom";
import { resendSignupCode } from "../actions/user";
import { AuthError } from "../utils/authErrors";

interface Props {
  email: string;
  /** Sign-in runs right after the code is accepted, so a new user never has
   *  to retype what they just entered. */
  onConfirm: (code: string) => Promise<unknown>;
  busy?: boolean;
  /** The login path arrives here days later, when the original code may have
   *  expired — say so instead of implying one just went out. */
  fromLogin?: boolean;
}

/**
 * The emailed-code step that real email verification needs. Errors from the
 * confirm attempt itself land in the parent's auth Alert (SIGNUP_FAILURE);
 * resend status is local because it never changes auth state.
 */
export default function ConfirmSignup({ email, onConfirm, busy, fromLogin }: Props) {
  const [code, setCode] = useState("");
  const [resending, setResending] = useState(false);
  const [resendResult, setResendResult] = useState<AuthError | "sent" | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!code.trim()) return;
    try {
      await onConfirm(code.trim());
    } catch {
      // Already surfaced through the parent's Alert.
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendResult(null);
    const error = await resendSignupCode(email);
    setResendResult(error ?? "sent");
    setResending(false);
  };

  return (
    <div>
      <h2>Check your email</h2>
      <p>
        {fromLogin
          ? "We sent a confirmation code to "
          : "We just sent a 6-digit code to "}
        <strong>{email}</strong>.{" "}
        {fromLogin
          ? "Enter it below, or send a new one if it's expired."
          : "Enter it below to finish creating your account."}
      </p>
      <form name="confirm" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="code">Confirmation code</label>
          <input
            type="text"
            className="form-control"
            id="code"
            name="code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={10}
            required
            autoFocus
            // Sized for Cognito's 6-digit code rather than the form's full width.
            style={{
              maxWidth: "9rem",
              fontFamily: "var(--font-mono)",
              fontSize: "1.25rem",
              letterSpacing: "0.2em",
              textAlign: "center",
            }}
          />
        </div>
        <div className="form-group">
          <button className="btn btn-primary" disabled={busy || !code.trim()}>
            {busy ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" /> Confirming...
              </>
            ) : (
              "Confirm and continue"
            )}
          </button>
        </div>
      </form>
      <p className="text-muted">
        Didn't get it? Check your spam folder, or{" "}
        <button
          type="button"
          className="btn btn-link p-0 align-baseline"
          onClick={handleResend}
          disabled={resending}
        >
          {resending ? "sending..." : "send a new code"}
        </button>
        .
      </p>
      {resendResult === "sent" && <p className="text-success">A new code is on its way.</p>}
      {resendResult && resendResult !== "sent" && (
        <p className="text-danger">
          {resendResult.message}
          {resendResult.action === "login" && (
            <> <Link to="/login">Log in</Link>.</>
          )}
        </p>
      )}
    </div>
  );
}
